import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { User, Mail, Building2, Shield, Key, Save } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  admin: 'Administrador',
  therapist: 'Terapeuta',
  read_only: 'Solo Lectura'
};

const ProfilePage = () => {
  const { user, checkAuth } = useAuth();
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    if (passwordForm.new_password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    try {
      await axios.put(
        `${API}/auth/change-password`,
        {
          current_password: passwordForm.current_password,
          new_password: passwordForm.new_password
        },
        { withCredentials: true }
      );
      
      toast.success('Contraseña actualizada correctamente');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
      setChangingPassword(false);
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      if (Array.isArray(errorMsg)) {
        toast.error(errorMsg.map(e => e.msg).join(', '));
      } else {
        toast.error(errorMsg || 'Error al cambiar contraseña');
      }
    }
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center h-96">
        <p>Cargando...</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold mb-6">Mi Perfil</h1>

      {/* User Info Card */}
      <Card className="p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center">
          <User className="h-5 w-5 mr-2" />
          Información Personal
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label className="text-gray-600">Nombre</Label>
            <p className="text-lg font-medium">{user.name}</p>
          </div>
          <div>
            <Label className="text-gray-600 flex items-center">
              <Mail className="h-4 w-4 mr-1" />
              Email
            </Label>
            <p className="text-lg font-medium">{user.email}</p>
          </div>
          <div>
            <Label className="text-gray-600 flex items-center">
              <Shield className="h-4 w-4 mr-1" />
              Rol
            </Label>
            <p className="text-lg font-medium">
              {ROLE_LABELS[user.role] || user.role}
            </p>
          </div>
          <div>
            <Label className="text-gray-600 flex items-center">
              <Building2 className="h-4 w-4 mr-1" />
              Institución
            </Label>
            <p className="text-lg font-medium">{user.institution_name}</p>
          </div>
        </div>
      </Card>

      {/* Change Password Card */}
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center">
          <Key className="h-5 w-5 mr-2" />
          Cambiar Contraseña
        </h2>

        {!changingPassword ? (
          <Button onClick={() => setChangingPassword(true)}>
            Cambiar Contraseña
          </Button>
        ) : (
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <Label htmlFor="current_password">Contraseña Actual</Label>
              <Input
                id="current_password"
                type="password"
                value={passwordForm.current_password}
                onChange={(e) =>
                  setPasswordForm({ ...passwordForm, current_password: e.target.value })
                }
                required
              />
            </div>
            <div>
              <Label htmlFor="new_password">Nueva Contraseña</Label>
              <Input
                id="new_password"
                type="password"
                value={passwordForm.new_password}
                onChange={(e) =>
                  setPasswordForm({ ...passwordForm, new_password: e.target.value })
                }
                required
                minLength={6}
              />
              <p className="text-xs text-gray-500 mt-1">Mínimo 6 caracteres</p>
            </div>
            <div>
              <Label htmlFor="confirm_password">Confirmar Nueva Contraseña</Label>
              <Input
                id="confirm_password"
                type="password"
                value={passwordForm.confirm_password}
                onChange={(e) =>
                  setPasswordForm({ ...passwordForm, confirm_password: e.target.value })
                }
                required
              />
            </div>
            <div className="flex space-x-2">
              <Button type="submit">
                <Save className="mr-2 h-4 w-4" />
                Guardar Contraseña
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setChangingPassword(false);
                  setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
                }}
              >
                Cancelar
              </Button>
            </div>
          </form>
        )}
      </Card>
    </div>
  );
};

export default ProfilePage;
